// src/components/VoiceAgent.js
// Interactive showcase of Isabella's AI voice/qualifier agent.
// Rebuilds the multi-step onboarding flow (goal -> audience -> ...) as an
// animated widget, then captures the lead into Supabase.
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { submitLead } from '../lib/submitLead';
import { downloadPlan } from '../lib/generatePlan';
import { Reveal, GradientMesh } from './ui/Motion';
import { IconCheck, IconArrowRight, IconEdit } from './ui/Icons';

/* ------------------------------------------------------------------ *
 * Branching qualifier — the way Isabella's intake agent would triage a
 * prospect before booking a call. Q1 (intent) drives the Q2 branch;
 * Q3 (scale) and Q4 (timeline) qualify fit.
 * ------------------------------------------------------------------ */

const INTENT = {
  key: 'intent',
  prompt: "Let's find the fastest win for you. What brings you here?",
  options: [
    { label: 'Growth & paid media', value: 'Growth & paid media', branch: 'growth' },
    { label: 'AI systems & automation', value: 'AI systems & automation', branch: 'ai' },
    { label: 'Build me a product', value: 'Build me a product', branch: 'product' },
    { label: "I'm not sure yet", value: 'Not sure yet', branch: 'unsure' },
  ],
};

const BRANCH_Q = {
  growth: {
    key: 'focus',
    prompt: "Got it — growth. Where's the biggest opportunity right now?",
    options: [
      'Scale paid ads (Google / Meta / TikTok)',
      'Build a high-converting funnel',
      'Fix tracking & attribution',
      'Bring my marketing in-house',
    ],
  },
  ai: {
    key: 'focus',
    prompt: 'Love it. Which AI system would move the needle most?',
    options: [
      'AI voice agent / 24-7 receptionist',
      'CRM automation & lead follow-up',
      'Data pipelines & reporting',
      'Prompt engineering / custom AI',
    ],
  },
  product: {
    key: 'focus',
    prompt: "Nice — let's build. What are you making?",
    options: [
      'An AI-powered SaaS product',
      'A mobile app (iOS / Android)',
      'An internal tool / automation',
      'An MVP from scratch',
    ],
  },
  unsure: {
    key: 'focus',
    prompt: "No problem — that's what I'm here for. What's the business goal?",
    options: [
      'More qualified leads',
      'Lower cost per acquisition',
      'Automate manual operations',
      'Launch something new',
    ],
  },
};

const SCALE = {
  key: 'scale',
  prompt: "What's your rough monthly marketing / project budget?",
  options: [
    'Under $5k',
    '$5k – $25k',
    '$25k – $100k',
    '$100k+',
  ],
};

const TIMELINE = {
  key: 'timeline',
  prompt: 'Last one — how soon are you looking to start?',
  options: [
    'ASAP / this month',
    'Next 1–3 months',
    'Just exploring for now',
  ],
};

/* Assemble the step list for the current answers (Q2 depends on intent). */
function buildSteps(answers) {
  const branch = INTENT.options.find((o) => o.value === answers.intent)?.branch;
  return [INTENT, branch ? BRANCH_Q[branch] : null, SCALE, TIMELINE].filter(Boolean);
}

const VoiceAgent = () => {
  const [started, setStarted] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [custom, setCustom] = useState('');
  const [editing, setEditing] = useState(false);
  const [contact, setContact] = useState({ name: '', company: '', email: '' });
  const [status, setStatus] = useState('idle'); // idle | saving | done | error

  const steps = useMemo(() => buildSteps(answers), [answers]);
  const isContactStep = started && stepIndex >= steps.length;
  const current = steps[stepIndex];

  const progress = useMemo(() => {
    // 4 questions + contact = 5 slots total (Q2 always exists once intent picked)
    const total = 5;
    const done = isContactStep ? steps.length : stepIndex;
    return Math.round((done / total) * 100);
  }, [stepIndex, isContactStep, steps.length]);

  const choose = (value) => {
    // Picking a new intent resets any later answers so the branch stays consistent.
    setAnswers((a) =>
      current.key === 'intent'
        ? { intent: value }
        : { ...a, [current.key]: value }
    );
    setEditing(false);
    setCustom('');
    setStepIndex((i) => i + 1);
  };

  const submitCustom = (e) => {
    e.preventDefault();
    if (!custom.trim()) return;
    choose(custom.trim());
  };

  const restart = () => {
    setStarted(false);
    setStepIndex(0);
    setAnswers({});
    setContact({ name: '', company: '', email: '' });
    setStatus('idle');
  };

  const finish = async (e) => {
    e.preventDefault();
    setStatus('saving');
    try {
      // Send the lead to Formspree (same inbox as other leads; tagged by source)
      await submitLead({
        _source: 'AI Agent qualifier (site demo)',
        _subject: 'New qualified lead from the site',
        name: contact.name,
        company: contact.company,
        email: contact.email,
        intent: answers.intent || '',
        focus: answers.focus || '',
        budget: answers.scale || '',
        timeline: answers.timeline || '',
      });
      setStatus('done');
    } catch (err) {
      // Fall back gracefully — still show the success screen to the visitor.
      console.error('Formspree submission failed:', err.message);
      setStatus('error');
    }

    // Either way, deliver the tailored plan they were promised.
    try {
      downloadPlan({ contact, answers });
    } catch (err) {
      console.error('Plan generation failed:', err.message);
    }
  };

  return (
    <Section id="voice-agent">
      <GradientMesh
        blobs={[
          { size: '40vw', top: '-10%', right: '-8%', color: '#8b5cf6', opacity: 0.22, blur: 120, dur: 24 },
          { size: '32vw', bottom: '-15%', left: '-6%', color: '#06b6d4', opacity: 0.2, blur: 120, dur: 28, delay: 4 },
        ]}
      />
      <Container>
        <Reveal>
          <Eyebrow>
            <Dot /> New — Voice AI
          </Eyebrow>
          <Title>
            I don't just run the ads.{' '}
            <span className="grad-animate">My AI answers the phone.</span>
          </Title>
          <Lead>
            One of the systems I build: a voice AI agent that captures every lead,
            qualifies it in a natural conversation, and books the appointment —
            24/7, no missed calls. Try a live version of its qualifier below.
          </Lead>
        </Reveal>

        <Grid>
          {/* Left: animated voice orb + capabilities */}
          <Reveal delay={0.1}>
            <OrbPanel>
              <OrbStage>
                <Ring1 />
                <Ring2 />
                <Orb animate={started ? 'listening' : 'idle'}>
                  <Bars>
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Bar
                        key={i}
                        animate={{
                          scaleY: started ? [0.4, 1, 0.5, 0.9, 0.4] : [0.5, 0.7, 0.5],
                        }}
                        transition={{
                          duration: started ? 0.9 : 2,
                          repeat: Infinity,
                          delay: i * 0.12,
                          ease: 'easeInOut',
                        }}
                      />
                    ))}
                  </Bars>
                </Orb>
              </OrbStage>
              <OrbStatus>
                <StatusDot $on={started} />
                {started ? 'Agent active — listening' : 'Agent ready'}
              </OrbStatus>
              <CapList>
                {[
                  '24/7 inbound call & lead capture',
                  'Natural-language qualification',
                  'Books appointments into your calendar',
                  'Syncs to CRM (GoHighLevel, HubSpot)',
                ].map((c) => (
                  <Cap key={c}>
                    <IconCheck size={16} />
                    {c}
                  </Cap>
                ))}
              </CapList>
            </OrbPanel>
          </Reveal>

          {/* Right: the interactive qualifier */}
          <Reveal delay={0.2}>
            <ChatPanel>
              <ChatHeader>
                <HeaderLeft>
                  <MiniOrb />
                  <div>
                    <HeaderName>Isabella's AI Agent</HeaderName>
                    <HeaderSub>Qualifier demo</HeaderSub>
                  </div>
                </HeaderLeft>
                {started && <Restart onClick={restart}>Restart</Restart>}
              </ChatHeader>

              <ProgressTrack>
                <ProgressBar
                  animate={{ width: `${started ? progress : 0}%` }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                />
              </ProgressTrack>

              <ChatBody>
                <AnimatePresence mode="wait">
                  {!started && (
                    <StepWrap key="intro" {...anim}>
                      <Bubble>
                        Hi — I'm Isabella's AI agent. Answer 4 quick questions and
                        I'll map out how she can help, then get you booked in.
                      </Bubble>
                      <StartBtn onClick={() => setStarted(true)} data-cursor="hover">
                        Let's go
                        <IconArrowRight size={16} />
                      </StartBtn>
                    </StepWrap>
                  )}

                  {started && !isContactStep && (
                    <StepWrap key={current.key} {...anim}>
                      <Bubble>{current.prompt}</Bubble>
                      <Options>
                        {current.options.map((opt, i) => {
                          const label = typeof opt === 'string' ? opt : opt.label;
                          const value = typeof opt === 'string' ? opt : opt.value;
                          return (
                            <OptionBtn
                              key={label}
                              onClick={() => choose(value)}
                              data-cursor="hover"
                              whileHover={{ x: 6 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <OptNum>{i + 1}</OptNum>
                              <span>{label}</span>
                              <OptArrow>→</OptArrow>
                            </OptionBtn>
                          );
                        })}

                        {!editing ? (
                          <OptionBtn
                            as="button"
                            $ghost
                            onClick={() => setEditing(true)}
                            data-cursor="hover"
                          >
                            <OptNum $ghost><IconEdit size={15} /></OptNum>
                            <span style={{ opacity: 0.7 }}>Something else…</span>
                          </OptionBtn>
                        ) : (
                          <CustomForm onSubmit={submitCustom}>
                            <CustomInput
                              autoFocus
                              placeholder="Type your answer…"
                              value={custom}
                              onChange={(e) => setCustom(e.target.value)}
                            />
                            <CustomSend type="submit" data-cursor="hover">
                              Send
                            </CustomSend>
                          </CustomForm>
                        )}
                      </Options>
                    </StepWrap>
                  )}

                  {isContactStep && status !== 'done' && status !== 'error' && (
                    <StepWrap key="contact" {...anim}>
                      <Bubble>
                        Perfect — I've got what I need. Where should I send your
                        tailored plan?
                      </Bubble>
                      <ContactForm onSubmit={finish}>
                        <Field
                          required
                          placeholder="Your name"
                          value={contact.name}
                          onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
                        />
                        <Field
                          required
                          placeholder="Company name"
                          value={contact.company}
                          onChange={(e) => setContact((c) => ({ ...c, company: e.target.value }))}
                        />
                        <Field
                          required
                          type="email"
                          placeholder="Work email"
                          value={contact.email}
                          onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                        />
                        <SubmitBtn type="submit" disabled={status === 'saving'} data-cursor="hover">
                          {status === 'saving' ? 'Sending…' : 'Get my plan'}
                        </SubmitBtn>
                      </ContactForm>
                    </StepWrap>
                  )}

                  {(status === 'done' || status === 'error') && (
                    <StepWrap key="done" {...anim}>
                      <SuccessMark
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 200, damping: 12 }}
                      >
                        <IconCheck size={26} />
                      </SuccessMark>
                      <Bubble>
                        Done, {contact.name.split(' ')[0] || 'there'}! Your tailored
                        plan just downloaded{contact.company ? ` for ${contact.company}` : ''} —
                        recommended channels, first moves, and a roadmap. The best
                        next step is 45 minutes with Isabella to turn it into action.
                      </Bubble>
                      <SummaryCard>
                        {contact.company && (
                          <SumRow><b>Company</b><span>{contact.company}</span></SumRow>
                        )}
                        <SumRow><b>Looking for</b><span>{answers.intent}</span></SumRow>
                        <SumRow><b>Priority</b><span>{answers.focus}</span></SumRow>
                        <SumRow><b>Budget</b><span>{answers.scale}</span></SumRow>
                        <SumRow><b>Timeline</b><span>{answers.timeline}</span></SumRow>
                      </SummaryCard>
                      <DoneActions>
                        <BookBtn
                          href="https://calendar.app.google/JNzzrs67cJGNb3Vq8"
                          target="_blank"
                          rel="noreferrer"
                          data-cursor="hover"
                        >
                          Book a strategy call
                          <IconArrowRight size={18} />
                        </BookBtn>
                        <GhostBtn
                          type="button"
                          onClick={() => downloadPlan({ contact, answers })}
                          data-cursor="hover"
                        >
                          Download my plan again
                        </GhostBtn>
                      </DoneActions>
                    </StepWrap>
                  )}
                </AnimatePresence>
              </ChatBody>
            </ChatPanel>
          </Reveal>
        </Grid>
      </Container>
    </Section>
  );
};

const anim = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
  transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
};

/* ---------------- styles ---------------- */

const Section = styled.section`
  position: relative;
  padding: 7rem 0;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.darker};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 4.5rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0 1.5rem;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #a78bfa;
  margin-bottom: 1rem;
`;

const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #8b5cf6;
  box-shadow: 0 0 10px #8b5cf6;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4.5vw, 3.2rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
  max-width: 800px;
  margin-bottom: 1.25rem;
`;

const Lead = styled.p`
  max-width: 620px;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 3.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: 2rem;
  align-items: stretch;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid ${({ theme }) => theme.colors.border};
  backdrop-filter: blur(16px);
`;

const OrbPanel = styled(Panel)`
  padding: 2.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const OrbStage = styled.div`
  position: relative;
  width: 180px;
  height: 180px;
  display: grid;
  place-items: center;
  margin: 1rem 0 1.5rem;
`;

const ringBase = `
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(139, 92, 246, 0.4);
`;

const Ring1 = styled.div`
  ${ringBase}
  inset: 0;
  animation: pulseRing 3s ease-out infinite;
`;
const Ring2 = styled.div`
  ${ringBase}
  inset: 20px;
  animation: pulseRing 3s ease-out infinite 1.5s;
`;

const Orb = styled(motion.div)`
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background: conic-gradient(from 0deg, #6366f1, #8b5cf6, #ec4899, #06b6d4, #6366f1);
  display: grid;
  place-items: center;
  box-shadow: 0 0 50px rgba(139, 92, 246, 0.5);
  animation: spinSlow 8s linear infinite;
`;

const Bars = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 40px;
  animation: spinSlow 8s linear infinite reverse; /* counter-spin so bars stay upright */
`;

const Bar = styled(motion.div)`
  width: 5px;
  height: 34px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.95);
  transform-origin: center;
`;

const OrbStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 1.75rem;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $on }) => ($on ? '#10b981' : '#64748b')};
  box-shadow: ${({ $on }) => ($on ? '0 0 10px #10b981' : 'none')};
  transition: all 0.3s ease;
`;

const CapList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  width: 100%;
`;

const Cap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.92rem;
  color: ${({ theme }) => theme.colors.textSecondary};

  svg { color: #10b981; flex-shrink: 0; }
`;

const ChatPanel = styled(Panel)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 460px;
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const MiniOrb = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: conic-gradient(from 0deg, #6366f1, #ec4899, #06b6d4, #6366f1);
  animation: spinSlow 6s linear infinite;
`;

const HeaderName = styled.div`
  font-weight: 700;
  font-size: 0.95rem;
`;

const HeaderSub = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Restart = styled.button`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { background: rgba(255, 255, 255, 0.12); color: #fff; }
`;

const ProgressTrack = styled.div`
  height: 3px;
  background: rgba(255, 255, 255, 0.06);
`;

const ProgressBar = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #ec4899);
`;

const ChatBody = styled.div`
  padding: 1.75rem 1.5rem;
  flex: 1;
`;

const StepWrap = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Bubble = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 16px 16px 16px 4px;
  padding: 1rem 1.25rem;
  font-size: 1.02rem;
  line-height: 1.5;
  max-width: 90%;
`;

const StartBtn = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(120deg, #6366f1, #ec4899);
  color: #fff;
  font-weight: 600;
  padding: 0.85rem 1.4rem;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  box-shadow: 0 6px 24px rgba(99, 102, 241, 0.4);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(236,72,153,0.45); }
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const OptionBtn = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  width: 100%;
  text-align: left;
  background: ${({ $ghost }) => ($ghost ? 'transparent' : 'rgba(255,255,255,0.04)')};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: 0.85rem 1rem;
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.95rem;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: rgba(139, 92, 246, 0.6);
    background: rgba(139, 92, 246, 0.08);
  }
  span { flex: 1; }
`;

const OptNum = styled.span`
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: ${({ $ghost }) => ($ghost ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg,#6366f1,#8b5cf6)')};
  font-size: 0.8rem;
  font-weight: 700;
  flex: 0 0 auto !important;
`;

const OptArrow = styled.span`
  opacity: 0;
  color: #a78bfa;
  transition: opacity 0.2s ease;
  ${OptionBtn}:hover & { opacity: 1; }
`;

const CustomForm = styled.form`
  display: flex;
  gap: 0.5rem;
`;

const CustomInput = styled.input`
  flex: 1;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: 12px;
  padding: 0.85rem 1rem;
  color: #fff;
  font-size: 0.95rem;
  &:focus { outline: none; border-color: #8b5cf6; }
`;

const CustomSend = styled.button`
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 0 1.2rem;
  font-weight: 600;
  cursor: pointer;
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Field = styled.input`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: 12px;
  padding: 0.95rem 1.1rem;
  color: #fff;
  font-size: 0.95rem;
  &:focus { outline: none; border-color: #ec4899; }
  &::placeholder { color: ${({ theme }) => theme.colors.textMuted}; }
`;

const SubmitBtn = styled.button`
  background: linear-gradient(120deg, #6366f1, #ec4899);
  color: #fff;
  font-weight: 700;
  padding: 1rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.2s ease;
  &:hover:not(:disabled) { transform: translateY(-2px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const SuccessMark = styled(motion.div)`
  align-self: flex-start;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981, #06b6d4);
  color: #fff;
  font-size: 1.6rem;
  font-weight: 800;
  display: grid;
  place-items: center;
  box-shadow: 0 0 30px rgba(16, 185, 129, 0.5);
`;

const SummaryCard = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 14px;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const SumRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.88rem;
  b { color: ${({ theme }) => theme.colors.textMuted}; font-weight: 600; }
  span { color: ${({ theme }) => theme.colors.text}; text-align: right; }
`;

const DoneActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
`;

const BookBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(120deg, #6366f1, #ec4899);
  color: #fff;
  font-weight: 600;
  padding: 0.85rem 1.4rem;
  border-radius: 9999px;
  transition: transform 0.2s ease;
  &:hover { transform: translateY(-2px); }
`;

const GhostBtn = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.8rem 1.2rem;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { color: #fff; border-color: rgba(255,255,255,0.4); }
`;

export default VoiceAgent;
